import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import SearchBar from '@/components/feature/SearchBar';
import MovieCard from '@/components/feature/MovieCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { searchMovies, getHotMovies, getMovieById } from '@/lib/movieUtils';
import { categoryGroups, sortOptions } from '@/mocks/categories';
import type { SortValue } from '@/components/feature/FilterBar';
import { BRAND_NAME } from '@/mocks/categories';

const regionQuick = ['中国大陆', '中国香港', '中国台湾', '韩国', '日本', '美国', '英国'];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const [typeFilter, setTypeFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [sort, setSort] = useState<SortValue>('latest');

  usePageMeta({
    title: keyword ? `搜索：${keyword} - ${BRAND_NAME}` : `资源搜索 - ${BRAND_NAME}`,
    description: keyword
      ? `在${BRAND_NAME}中搜索「${keyword}」相关的国语对白影音资源，快速定位想看的影视作品。`
      : `在${BRAND_NAME}中输入关键词，搜索国语对白电影、电视剧、综艺与纪录片资源。`,
    keywords: `${keyword || '搜索'},国语对白,影音资源,${BRAND_NAME}`,
  });

  useEffect(() => {
    setTypeFilter('');
    setRegionFilter('');
    setSort('latest');
  }, [keyword]);

  const results = useMemo(() => {
    if (!keyword) return [];
    let list = searchMovies(keyword);
    if (typeFilter) list = list.filter((m) => m.type === typeFilter);
    if (regionFilter) list = list.filter((m) => m.region === regionFilter);
    const sorted = [...list];
    if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === 'hot') sorted.sort((a, b) => b.playCount - a.playCount);
    else if (sort === 'year') sorted.sort((a, b) => b.year - a.year);
    else sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    return sorted;
  }, [keyword, typeFilter, regionFilter, sort]);

  const recommendations = getHotMovies(4)
    .map((m) => getMovieById(m.id))
    .filter(Boolean);

  const pill = (active: boolean) =>
    `px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
      active ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
    }`;

  return (
    <PageShell>
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-3xl">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">资源搜索</h1>
          <p className="mt-2 text-sm text-foreground-600">
            输入影视名称、主演或关键词，快速查找国语对白影音资源。
          </p>
          <div className="mt-5">
            <SearchBar size="lg" defaultKeyword={keyword} />
          </div>
        </div>

        {!keyword ? (
          <div className="mt-8 py-16 text-center">
            <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-700">输入关键词，开始你的资源查找</p>
            <p className="mt-2 text-sm text-foreground-500">试试「悬疑」「家族」「自然」等关键词</p>
          </div>
        ) : (
          <>
            <div className="mt-6 flex items-center flex-wrap gap-2">
              <span className="text-sm text-foreground-500">当前关键词：</span>
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium">
                {keyword}
              </span>
            </div>

            <div className="mt-5 bg-background-50 border border-background-200 rounded-lg p-4 space-y-3">
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <span className="text-sm text-foreground-500 md:w-16 shrink-0">分类</span>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setTypeFilter('')} className={pill(typeFilter === '')}>
                    全部
                  </button>
                  {categoryGroups.map((g) => (
                    <button
                      key={g.type}
                      type="button"
                      onClick={() => setTypeFilter(g.type)}
                      className={pill(typeFilter === g.type)}
                    >
                      {g.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <span className="text-sm text-foreground-500 md:w-16 shrink-0">地区</span>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setRegionFilter('')} className={pill(regionFilter === '')}>
                    全部
                  </button>
                  {regionQuick.map((region) => (
                    <button
                      key={region}
                      type="button"
                      onClick={() => setRegionFilter(region)}
                      className={pill(regionFilter === region)}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-foreground-500">
                找到 <span className="text-foreground-900 font-semibold">{results.length}</span> 条相关资源
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">排序</span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortValue)}
                  className="h-9 px-3 pr-8 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {results.length === 0 ? (
              <div className="mt-8 py-16 text-center">
                <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
                <p className="mt-4 text-foreground-700">没有找到与「{keyword}」相关的资源</p>
                <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐</p>
                <div className="mt-8 max-w-[960px] mx-auto text-left">
                  <h2 className="text-lg font-bold text-foreground-950 mb-4">你可能感兴趣</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                    {recommendations.map((movie) =>
                      movie ? <MovieCard key={movie.id} movie={movie} showSummary={false} /> : null,
                    )}
                  </div>
                </div>
                <Link
                  to="/"
                  className="mt-8 inline-flex items-center gap-1.5 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  返回首页
                </Link>
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
}